import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { LearnArticleLink, LearnSeriesNav } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.de-idea-a-negocio-paso-1-viabilidad.${field}`

const SOURCES = [
    {
        href: 'https://www.infohoreca.com/noticias/20260522/apertura-restaurantes-consejos-errores-linkers',
        labelKey: 'sources.1',
        fallback:
            'InfoHoreca, "El 60% de los restaurantes cierra antes de un año: los errores ocultos que están arruinando la hostelería española", 22 de mayo de 2026, con datos de la consultora Linkers.',
    },
] as const

export function DeIdeaANegocioPaso1ViabilidadArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        {
            id: 'ya-toca',
            label: t(k('toc.1'), '1. Llevas años en cocinas de otros. Un día decides que ya toca la tuya'),
        },
        {
            id: 'cuatro-medios',
            label: t(k('toc.2'), '2. Los cuatro medios que necesitas antes de montar un negocio de hostelería'),
        },
        { id: 'sentido', label: t(k('toc.3'), '3. ¿Tiene sentido que lo montes tú?') },
        {
            id: 'tiempo-rojo',
            label: t(k('toc.4'), '4. ¿Cuánto tiempo vas a estar perdiendo dinero antes de ganarlo?'),
        },
        {
            id: 'cuenta-vieja',
            label: t(k('toc.5'), '5. Seis de cada diez no llegan al año'),
        },
        {
            id: 'ejemplo',
            label: t(k('toc.6'), '6. Un ejemplo con números: el mismo bar, dos puntos de partida'),
        },
        { id: 'esta-semana', label: t(k('toc.7'), '7. Seis comprobaciones antes de firmar nada') },
        { id: 'matices', label: t(k('toc.8'), '8. Lo que no se puede fijar con un número único') },
    ]

    const weekItems = [
        t(
            k('week.1'),
            'Escribe tus cuatro medios en una hoja: dinero, oficio, mercado, ubicación. Puntúa cada uno del 1 al 5, con honestidad.',
        ),
        t(
            k('week.2'),
            'Si algo puntúa 2 o menos, decide si lo vas a resolver (formación, socio, cambiar de zona) antes de seguir adelante.',
        ),
        t(
            k('week.3'),
            'Calcula cuántos meses de gastos personales y del negocio aguantas sin facturar lo suficiente. Si no llegas a seis, no montes sociedad todavía.',
        ),
        t(
            k('week.4'),
            'Habla con dos o tres negocios parecidos al tuyo, aunque no sean competencia directa, y pregúntales cuánto tardaron en dejar de perder dinero.',
        ),
        t(
            k('week.5'),
            'Cuenta cuántos locales parecidos al tuyo hay cerca de donde quieres montarlo, y a qué precio venden.',
        ),
        t(k('week.6'), 'No reserves local ni firmes nada hasta tener esa lista respondida por escrito.'),
    ]

    const mediosRows = [
        {
            medio: t(k('table.row.1.medio'), 'Dinero'),
            pregunta: t(k('table.row.1.pregunta'), '¿Aguantas varios meses sin ganar, además de la inversión inicial?'),
        },
        {
            medio: t(k('table.row.2.medio'), 'Oficio'),
            pregunta: t(k('table.row.2.pregunta'), '¿Sabes ejecutar tú lo que vendes: cocinar, servir, atender?'),
        },
        {
            medio: t(k('table.row.3.medio'), 'Mercado'),
            pregunta: t(k('table.row.3.pregunta'), '¿Conoces la demanda real de esa idea, no solo si te gusta a ti?'),
        },
        {
            medio: t(k('table.row.4.medio'), 'Ubicación'),
            pregunta: t(
                k('table.row.4.pregunta'),
                '¿Conoces esa zona: quién vive, quién pasa, quién vende ya lo mismo?',
            ),
        },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Primer capítulo de nuestra guía «De idea a negocio», donde vamos paso a paso desde que tienes una idea hasta que abres la persiana.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Interior de un bar vacío antes de abrir, sillas encima de las mesas y cajas de cristalería junto a la ventana',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                <span className={articleUi.em}>
                                    {t(
                                        k('lead'),
                                        'El 60 % de los restaurantes que abren en España no llega a cumplir un año',
                                    )}
                                </span>
                                {t(
                                    k('lead.rest'),
                                    ', casi siempre por decisiones que se tomaron, o no se tomaron, antes de abrir la puerta. Antes del local, antes del gestor y antes de la sociedad, hay una pregunta que decide el resto: ¿tienes los medios (dinero, oficio, mercado y ubicación) para montar un negocio de hostelería, y de verdad te va a hacer feliz montarlo?',
                                )}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='ya-toca' className={articleUi.h2}>
                        {t(k('h2.yaToca'), 'Llevas años en cocinas de otros. Un día decides que ya toca la tuya')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.yaToca.1'),
                                'Tienes la idea clara: la carta, el género, hasta el nombre en la fachada.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.yaToca.2'),
                                'Lo que no tienes tan claro es si vas a poder pagar el alquiler en el mes tres, si tu ciudad ya tiene seis sitios como el tuyo, o si aguantas seis meses en rojo antes de que arranque.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.yaToca.3'),
                                'Nadie te hace esas preguntas antes de firmar. Este es el primer paso de la guía.',
                            )}
                        </p>
                    </div>

                    <h2 id='cuatro-medios' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(
                            k('h2.cuatroMedios'),
                            'Los cuatro medios que necesitas antes de montar un negocio de hostelería',
                        )}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cuatroMedios.1'),
                                'Tener medios no es solo tener ahorros. Son cuatro cosas, y cada una falla por su cuenta.',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.tableWrap}>
                        <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                            <caption className='sr-only'>
                                {t(k('table.caption'), 'Los cuatro medios y la pregunta que responde cada uno')}
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.col.medio'), 'Medio')}</th>
                                    <th scope='col'>{t(k('table.col.pregunta'), 'La pregunta que responde')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mediosRows.map(row => (
                                    <tr key={row.medio}>
                                        <th scope='row'>{row.medio}</th>
                                        <td>{row.pregunta}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cuatroMedios.2'),
                                'Te puede faltar solo uno y el proyecto entero se resiente. Sobra oficio y falta mercado: cocinas de miedo para nadie. Sobra dinero y falta ubicación: montas el sitio perfecto donde nadie te va a encontrar.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.cuatroMedios.3'),
                                '"Conocer el mercado" no es intuición: es haber contado cuántos locales parecidos al tuyo hay a diez minutos andando, qué precio cobran y cuánta gente entra un martes cualquiera. "Conocer la ubicación" es lo mismo aplicado a la calle: quién vive alrededor, quién trabaja allí, y si esa combinación puede pagar lo que vas a cobrar.',
                            )}
                        </p>
                    </div>

                    <h2 id='sentido' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.sentido'), '¿Tiene sentido que lo montes tú?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.sentido.2'),
                                'No es lo mismo una barra de tapas en tu barrio que un omakase en Japón. Lo segundo exige oficio allí, contactos y conocimiento real de ese mercado, no solo ilusión.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.sentido.3'),
                                'Si la única respuesta es "porque me apetece", todavía no lo has puesto a prueba. Eso es justo este primer paso.',
                            )}
                        </p>
                    </div>

                    <h2 id='tiempo-rojo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.tiempoRojo'), '¿Cuánto tiempo vas a estar perdiendo dinero antes de ganarlo?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.tiempoRojo.1'),
                                'Casi ningún negocio de hostelería sale ganando desde el primer mes. Lo normal es un arranque de varios meses en números rojos mientras se hace la clientela y se rueda el equipo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.tiempoRojo.2'),
                                'Eso no es un fallo tuyo. Es la curva normal del negocio. El fallo está en no haberlo previsto en la caja antes de montar sociedad.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.tiempoRojo.3Em'),
                                    'El plan de negocio y el estudio de viabilidad van antes de la sociedad, no después.',
                                )}
                            </span>{' '}
                            {t(
                                k('p.tiempoRojo.3'),
                                'No son un trámite para el banco: son la única forma de saber, con números propios, si tus ahorros dan para llegar al mes en que el negocio se sostiene solo. No se puede empezar la casa por el tejado.',
                            )}
                        </p>
                    </div>

                    <h2 id='cuenta-vieja' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.cuentaVieja'), 'Seis de cada diez no llegan al año')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cuentaVieja.1'),
                                'En España abren cada año más de 15.000 negocios nuevos de hostelería. Y según los datos de la consultora Linkers recogidos por InfoHoreca, hasta el 60 % no logra sobrevivir a su primer año de vida.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.cuentaVieja.2'),
                                'El motivo casi nunca es la cocina: es un concepto sin comprobar si había mercado al precio que hacía falta cobrar. Ese 60 % es saltarse este paso, multiplicado por miles de aperturas.',
                            )}
                        </p>
                    </div>

                    <h2 id='ejemplo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.ejemplo'), 'Un ejemplo con números: el mismo bar, dos puntos de partida')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.ejemplo.1'),
                                'Dos personas quieren montar el mismo bar de tapas en la misma calle.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.ejemplo.2'),
                                'La primera tiene 15.000 € ahorrados, ha trabajado ocho años de camarero y cocinero, y lleva toda la vida en ese barrio. La segunda tiene los mismos 15.000 €, pero nunca ha trabajado en hostelería y quiere montarlo en una ciudad que no conoce, porque vio un local barato en un anuncio.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.ejemplo.3'),
                                'Con la misma inversión, la primera cubre tres de los cuatro medios de salida: dinero, oficio y ubicación. Solo le falta validar el mercado con datos, no con intuición. A la segunda le falta oficio y le falta ubicación, y el dinero que tiene no compensa lo que le falta en las otras dos.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.ejemplo.4'),
                                'No es que el proyecto de la segunda persona esté prohibido. Es que antes de invertir un euro, tiene una lista de deberes que la primera ya se ha ahorrado.',
                            )}
                        </p>
                    </div>

                    <h2 id='esta-semana' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.estaSemana'), 'Seis comprobaciones antes de firmar nada')}
                    </h2>
                    <ol className={articleUi.numListGap2xl}>
                        {weekItems.map(item => (
                            <li key={item} className={articleUi.numItem}>
                                {item}
                            </li>
                        ))}
                    </ol>

                    <h2 id='matices' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.matices'), 'Lo que no se puede fijar con un número único')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.1'),
                                'La inversión inicial de un negocio de hostelería varía muchísimo: hemos visto cifras de mercado que van de 20.000 € para un bar modesto a más de 300.000 € para un restaurante grande en zona prime. No existe un número único, y cualquiera que te lo dé sin conocer tu concepto y tu ciudad se lo está inventando.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.matices.2before'),
                                'Tampoco hay un plazo estándar para dejar de perder dinero: depende del concepto, del alquiler y de cuánto tarda en correrse la voz. El siguiente capítulo entra en el ',
                            )}
                            <LearnArticleLink slug='plan-viabilidad-hosteleria-primeros-pasos'>
                                {t(k('p.matices.2link'), 'plan de viabilidad con números')}
                            </LearnArticleLink>
                            {t(
                                k('p.matices.2after'),
                                ': concepto, mercado, costes y punto de equilibrio antes de firmar un local.',
                            )}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={articleUi.link}
                                >
                                    {t(k(source.labelKey), source.fallback)}
                                </a>
                            </li>
                        ))}
                        <li>
                            {t(
                                k('sources.2'),
                                'Elaboración propia de Tipi: marco de los cuatro medios y ejemplo con números.',
                            )}
                        </li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
